import config from '../config';

/**
 * Direct Cognito Authentication Service  (Optimized)
 * For running on absolutevision.in
 * Performance-optimized for 10,000+ concurrent users
 * No flickering, smooth animations, efficient state management
 */

class CognitoAuthService {
  constructor() {
    this.tokenRefreshInterval = null;
    this.tokenRefreshBuffer = 2 * 60 * 1000; // 2 minutes before expiry
    this.setupTokenRefreshListener();
  }

  /**
   * Setup listener for token refresh failures
   */
  setupTokenRefreshListener() {
    window.addEventListener('tokenRefreshFailed', () => {
      this.clearTokens();
      window.location.href = '/';
    });
  }

  /**
   * Direct authentication with Cognito via backend
   * Backend handles AdminInitiateAuth securely
   */
  async authenticateUser(username, password) {
    try {
      const response = await fetch(`${config.api.auth_backend}/api/auth/cognito-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include', // Important for CORS
        body: JSON.stringify({
          username,
          password,
          clientId: config.cognito.client_id
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `Authentication failed (${response.status}). Please check your credentials.`
        );
      }

      const data = await response.json();

      if (data.accessToken) {
        this.setTokens(data);
        this.setupTokenRefresh(data.expiresIn);
        
        return {
          success: true,
          user: data.user || this.getUserInfo(),
          tokens: {
            accessToken: data.accessToken,
            idToken: data.idToken,
            refreshToken: data.refreshToken
          }
        };
      }

      throw new Error('No access token received');
    } catch (error) {
      console.error('Authentication failed:', error);
      throw error;
    }
  }

  /**
   * Store tokens securely and efficiently
   */
  setTokens(tokenData) {
    const timestamp = new Date().getTime();
    
    // Use sessionStorage for sensitive tokens (cleared on browser close)
    sessionStorage.setItem('accessToken', tokenData.accessToken);
    sessionStorage.setItem('idToken', tokenData.idToken || '');
    sessionStorage.setItem('refreshToken', tokenData.refreshToken || '');
    sessionStorage.setItem('tokenTimestamp', timestamp.toString());
    sessionStorage.setItem('expiresIn', (tokenData.expiresIn || 3600).toString());
  }

  /**
   * Get access token
   */
  getAccessToken() {
    return sessionStorage.getItem('accessToken');
  }

  /**
   * Check if token is valid
   */
  isTokenValid() {
    const accessToken = this.getAccessToken();
    if (!accessToken) return false;

    try {
        const tokenTimestamp = parseInt(sessionStorage.getItem('tokenTimestamp') || '0');
      const expiresIn = parseInt(sessionStorage.getItem('expiresIn') || '3600');
      const currentTime = new Date().getTime();
      const tokenExpiry = tokenTimestamp + (expiresIn * 1000);

      return currentTime < tokenExpiry;
    } catch (err) {
      return false;
    }
  }

  /**
   *Setup automatic token refresh
   */
  setupTokenRefresh(expiresIn) {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
    }

    const refreshTimeMs = (expiresIn * 1000) - this.tokenRefreshBuffer;
    
    if (refreshTimeMs > 0) {
      this.tokenRefreshInterval = setInterval(() => {
        this.refreshToken().catch(() => {
          // Silent fail, user will be prompted to relogin on next action
        });
      }, Math.max(refreshTimeMs, 60000));
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken() {
    try {
      const refreshToken = sessionStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');

      const response = await fetch(`${config.api.auth_backend}/api/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ refreshToken })
      });

      if (response.ok) {
        const data = await response.json();
        this.setTokens(data);
        return data.accessToken;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.clearTokens();
      window.dispatchEvent(new CustomEvent('tokenRefreshFailed'));
    }
  }

  /**
   * Clear all stored tokens
   */
  clearTokens() {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('idToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('tokenTimestamp');
    sessionStorage.removeItem('expiresIn');
    
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
    }
  }

  /**
   * Get user info from ID token
   */
  getUserInfo() {
    try {
      const idToken = sessionStorage.getItem('idToken');
      if (!idToken) return null;

      const parts = idToken.split('.');
      if (parts.length !== 3) return null;

      return JSON.parse(atob(parts[1]));
    } catch (error) {
      return null;
    }
  }

  /**
   * Check authentication status
   */
  isAuthenticated() {
    return this.isTokenValid();
  }

  /**
   * Redirect to dashboard with token
   */
  redirectToDashboard() {
    const dashboardUrl = config.api.dashboard_url;
    const token = this.getAccessToken();
    
    if (token) {
      // Append token to URL for dashboard to pick up
      window.location.href = `${dashboardUrl}?token=${encodeURIComponent(token)}`;
    } else {
      window.location.href = dashboardUrl;
    }
  }
}

export default new CognitoAuthService();
